import { useState, useEffect, useCallback, useMemo } from 'react'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import {
  WbsItemFormModel,
  createInitialValue,
  toCreateInput,
  toDeltaInput,
} from '../../model'
import { useDispatch } from 'react-redux'
import { addScreenMessage, MessageLevel } from '../../../../../store/messages'
import { intl } from '../../../../../i18n'
import { ReferencedTicketListEntity } from '../../../../../domain/entity/TicketListEntity'
import {
  isReferencedEntityValueDiffered,
  toReferencedEntityDeltaValue,
} from '../../model/properties'
import {
  createTicketInitialValue,
  TicketFormModel,
  TicketInitialValue,
} from '../../model/ticket'
import {
  CreateTicketInput,
  CreateTicketRequest,
  TicketEntity,
} from '../../../../../domain/entity/ticket'
import { useTicketRepository } from '../../../../../services/adaptors/ticketRepositoryAdaptor'
import { usePageDataIsEdited } from '../../../../hooks/usePageDataIsEdited'
import { TicketRepository } from '../../../../../applications/ports/ticketRepository'
import { WbsItemTypeVO } from '../../../../../domain/value-object/WbsItemTypeVO'
import {
  FormModelModifiers,
  ReferencedEntityModifier,
} from '../../../../containers/SingleSheetV2/model/formModelModifiers'
import {
  wbsItemAdditionalPropertyValuesVoService,
  WbsItemAdditionalPropertyValueType,
} from '../../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemAdditionalPropertyEntity } from '../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { useEditedFormData } from '../../../../containers/SingleSheetV2/hooks/editedFormData'
import { APIResponse } from '../../../../../lib/commons/api'
import { ErrorCode } from '../../../../../handlers/globalErrorHandler'

export const useTicketSingleSheetData = (
  uuid: string,
  _wbsItemTypeVo: WbsItemTypeVO,
  _ticketType: string,
  ticketInitialValue?: TicketInitialValue
) => {
  const repository = useTicketRepository()
  const [wbsItemTypeVo, setWbsItemTypeVo] =
    useState<WbsItemTypeVO>(_wbsItemTypeVo)
  const [ticketType, setTicketType] = useState<string>(_ticketType)
  const [ticketTypeIsChanged, setTicketTypeIsChanged] = useState<boolean>(false)
  const [initialized, setInitialized] = useState<boolean>(false)
  const [isCreatingWbsItem, setIsCreatingWbsItem] = useState(false)
  const { edited, editedPageData, refreshedPageData } = usePageDataIsEdited()
  const { editedFormData, editFormData, refreshEditedFormData } =
    useEditedFormData()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [entity, setEntity] = useState<TicketEntity | undefined>()
  const [wbsItemFormModel, setWbsItemFormModel] = useState<WbsItemFormModel>(
    createInitialValue(
      WbsItemType.TASK,
      wbsItemTypeVo,
      ticketInitialValue?.wbsItem
    )
  )
  const [ticketFormModel, setTicketFormModel] = useState<TicketFormModel>(
    createTicketInitialValue('', ticketInitialValue)
  )
  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await repository.fetchByWbsItemUuid(uuid)
      setEntity(response)
      const formModel = structuredClone(response)
      setWbsItemFormModel(formModel.wbsItem)
      setWbsItemTypeVo(prev => {
        if (prev.uuid === response.wbsItem.typeVo.uuid) {
          return prev
        } else {
          return response.wbsItem.typeVo
        }
      })
      setTicketFormModel(formModel)
      setTicketType(prev => {
        if (prev === formModel.ticketType) {
          return prev
        } else {
          return formModel.ticketType
        }
      })
      setTicketTypeIsChanged(false)
    } catch (err: any) {
      if (err.code === 'NOT_FOUND' && ticketInitialValue) {
        // Ignore error because this wbs is unsaved.
        setIsCreatingWbsItem(true)
      } else {
        throw err
      }
    }
    setInitialized(true)
    setIsLoading(false)
    refreshedPageData()
    refreshEditedFormData()
  }, [
    repository,
    uuid,
    refreshedPageData,
    refreshEditedFormData,
    ticketInitialValue,
  ])
  useEffect(() => {
    fetchData()
  }, [fetchData, uuid])
  const onChangeWbsItem = useCallback(
    <K extends keyof WbsItemFormModel, T extends WbsItemFormModel[K]>(
      path: K,
      value: T
    ) => {
      setWbsItemFormModel(prev => {
        const newModel = {
          ...prev,
        }
        newModel[path] = value
        return newModel
      })
      editedPageData()
      editFormData(path)
    },
    [editedPageData, editFormData]
  )
  const onChangeAdditionalPropertyValue = useCallback(
    (
      wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
      value: WbsItemAdditionalPropertyValueType
    ) => {
      setWbsItemFormModel(prev => {
        let vo =
          prev.additionalPropertyValues ||
          wbsItemAdditionalPropertyValuesVoService.construct()
        wbsItemAdditionalPropertyValuesVoService.setValue(
          vo,
          wbsItemAdditionalProperty,
          value
        )
        return {
          ...prev,
          additionalPropertyValues: vo,
        }
      })
      editedPageData()
      editFormData(wbsItemAdditionalProperty.uuid)
    },
    [editedPageData]
  )

  const onChangeTicketList = useCallback(
    (ticketList: ReferencedTicketListEntity | undefined) => {
      if (!ticketList) return
      setTicketFormModel(prev => ({
        ...prev,
        ticketList,
        ticketType: ticketList.ticketType,
      }))
      editedPageData()
      editFormData('ticketList')
      setTicketTypeIsChanged(
        !isCreatingWbsItem && ticketType !== ticketList.ticketType
      )
    },
    [isCreatingWbsItem, ticketType, editedPageData, editFormData]
  )
  const ticketModelModifiers = useMemo<FormModelModifiers>(
    () => ({ ticketList: onChangeTicketList as ReferencedEntityModifier }),
    [onChangeTicketList]
  )
  const dispatch = useDispatch()
  const create = useCreate(
    dispatch,
    wbsItemTypeVo,
    ticketFormModel,
    wbsItemFormModel,
    repository,
    ticketInitialValue
  )
  const updateDelta = useUpdateDelta(
    dispatch,
    ticketFormModel,
    wbsItemFormModel,
    repository,
    entity
  )
  const showSuccessMessage = useCallback(() => {
    dispatch(
      addScreenMessage('wbsItem', {
        type: MessageLevel.SUCCESS,
        title: intl.formatMessage({
          id: isCreatingWbsItem
            ? 'create.completed.title'
            : 'update.completed.title',
        }),
      })
    )
  }, [dispatch, isCreatingWbsItem])
  const submit = useCallback(async () => {
    setIsLoading(true)
    try {
      let response: APIResponse | undefined = undefined
      if (isCreatingWbsItem) {
        const result = await create()
        response = result?.response
      } else {
        const result = await updateDelta()
        response = result?.response
      }
      showSuccessMessage()
      await fetchData()
      isCreatingWbsItem && setIsCreatingWbsItem(false)
    } catch (err: any) {
      if (err.code === ErrorCode.BUSINESS_ERROR) {
        const message = intl.formatMessage(
          { id: 'global.warning.updateFailed' },
          {
            code: wbsItemFormModel.code,
            name: wbsItemFormModel.displayName,
            option: '',
          }
        )
        dispatch(
          addScreenMessage('wbsItem', {
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: `-${message}\n-${err.detail}`,
          })
        )
        return
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [
    dispatch,
    isCreatingWbsItem,
    wbsItemFormModel,
    fetchData,
    create,
    updateDelta,
    showSuccessMessage,
  ])

  return {
    initialized,
    isCreatingWbsItem,
    edited,
    editedFormData,
    isLoading,
    wbsItemFormModel,
    wbsItemType: wbsItemTypeVo,
    ticketFormModel,
    ticketType,
    ticketTypeIsChanged,
    onChangeWbsItem,
    onChangeAdditionalPropertyValue,
    onChangeTicketList,
    ticketModelModifiers,
    reload: fetchData,
    submit,
  }
}

// Private hooks.
const useCreate = (
  dispatch: ReturnType<typeof useDispatch>,
  wbsItemTypeVo: WbsItemTypeVO,
  ticketFormModel: TicketFormModel,
  wbsItemFormModel: WbsItemFormModel,
  repository: TicketRepository,
  ticketInitialValue?: TicketInitialValue
) => {
  const create = useCallback(async () => {
    const projectUuid = ticketInitialValue?.wbsItem?.projectUuid || ''
    const wbsItem = toCreateInput(wbsItemFormModel, wbsItemTypeVo)
    const input: CreateTicketInput = {
      uuid: ticketFormModel.uuid,
      projectUuid,
      ticketType: ticketFormModel.ticketType,
      ticketListUuid: ticketFormModel.ticketList?.uuid || '',
      parentUuid: undefined,
      parentWbsItemUuid: ticketInitialValue?.parentWbsItem?.uuid,
      prevSiblingUuid: undefined,
      projectPlan: {
        uuid: ticketInitialValue?.projectPlanUuid || '',
        projectUuid,
        parentUuid: undefined,
        prevSiblingUuid: undefined,
        type: wbsItem.type,
        wbsItem,
      },
    }
    const request: CreateTicketRequest = {
      input,
      watchers: (wbsItemFormModel.watchers || []).map(watcher => watcher.uuid),
      tags: {
        wbsItemUuid: wbsItemFormModel.uuid,
        tagUuids: (wbsItemFormModel.tags || []).map(tag => tag.uuid),
      },
    }
    const result = await repository.create(request)
    return result
  }, [
    dispatch,
    ticketFormModel.uuid,
    ticketFormModel.ticketType,
    ticketFormModel.ticketList,
    wbsItemFormModel,
    repository,
    ticketInitialValue,
  ])

  return create
}
const useUpdateDelta = (
  dispatch: ReturnType<typeof useDispatch>,
  ticketFormModel: TicketFormModel,
  wbsItemFormModel: WbsItemFormModel,
  repository: TicketRepository,
  entity?: TicketEntity
) => {
  const updateDelta = useCallback(async () => {
    if (!entity) return
    const wbsItem = toDeltaInput(entity.wbsItem, wbsItemFormModel)
    const input = {
      uuid: entity.uuid,
      ticketListUuid: isReferencedEntityValueDiffered(
        entity.ticketList,
        ticketFormModel.ticketList
      )
        ? toReferencedEntityDeltaValue(
            entity.ticketList,
            ticketFormModel.ticketList
          )
        : undefined,
      wbsItem,
    }
    const request = {
      input,
      watchers: (wbsItemFormModel.watchers || []).map(watcher => watcher.uuid),
      tags: {
        wbsItemUuid: wbsItemFormModel.uuid,
        tagUuids: (wbsItemFormModel.tags || []).map(tag => tag.uuid),
      },
    }
    const result = await repository.updateDelta(request)
    return result
  }, [
    dispatch,
    entity,
    ticketFormModel.ticketList,
    wbsItemFormModel,
    repository,
  ])

  return updateDelta
}
